import type { QueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/shared/stores/auth-store';
import type { InboundEvent } from './encaixe-de-mensagem';
import { applyTicketCard, findTicketCard } from './inbox-debug';
import type { TicketSummary } from './types';

/** Preview textual pra mensagens de mídia (card da inbox + fallbacks). */
export function mediaPreviewForType(type: string): string | null {
  switch (type) {
    case 'image':
      return '[imagem]';
    case 'audio':
      return '[áudio]';
    case 'video':
      return '[vídeo]';
    case 'document':
      return '[documento]';
    case 'sticker':
      return '[sticker]';
    case 'location':
      return '📍 Localização';
    case 'contacts':
      return '👤 Contato';
    default:
      return null;
  }
}

/**
 * Remenda o CARD da lista a partir do evento `message.received`: preview novo,
 * badge de não-lida, status (quando o servidor mandou) e a subida pro topo —
 * o `applyTicketCard` redistribui nas listas em cache respeitando cada visão.
 *
 * Extraído do `onInboundInbox` (closure do useRealtimeListeners) pra ser usável
 * TAMBÉM pelo handler global (useDesktopNotifications) quando a página de
 * Atendimento está desmontada — sem isso, voltar pro Atendimento pintava a
 * lista na ordem velha e o refetch reordenava DIANTE do atendente (o card
 * "viajando" pro topo, medido no rastro 2026-07-24).
 *
 * Devolve `false` quando o ticket não está em nenhuma lista carregada — aí só
 * o servidor tem o cartão/total certos e o chamador decide o fallback (a
 * página invalida a lista; o global deixa o refetch natural de quem voltar).
 */
export function encaixaCardDoInbound(
  qc: QueryClient,
  evt: InboundEvent,
  openTicketId: number | null,
): boolean {
  const found = findTicketCard(qc, evt.ticket_id);
  const me = useAuthStore.getState().user;
  if (!found || !me) return false;
  // Worker às vezes emite body="" (string vazia) em vez de null pra
  // mídia sem caption. `??` só cai no fallback em null/undefined,
  // então normalizamos "" → null antes pra acionar o placeholder.
  const isStructured = evt.type === 'location' || evt.type === 'contacts';
  const bodyOrNull = !isStructured && evt.body && evt.body.trim().length > 0 ? evt.body : null;
  // Badge de não-lida:
  // - inbound do contato (ticket não aberto aqui) → +1
  // - outbound HUMANO (resposta no painel OU pelo celular/dispositivo
  //   externo do dono) = "já vi" → ZERA o badge
  // - bot outbound → mantém (respondeu sozinho, humano ainda não viu)
  // - ticket aberto aqui → mantém (markRead zera)
  let nextUnread = found.unread_count;
  if (evt.direction === 'outbound') {
    if (evt.bot_active !== true) nextUnread = 0;
  } else if (openTicketId !== evt.ticket_id) {
    nextUnread = found.unread_count + 1;
  }
  const card: TicketSummary = {
    ...found,
    /**
     * A ABA ABERTA NÃO DECIDE O STATUS DE UM ATENDIMENTO.
     *
     * O cartão em cache é a verdade (o motor o mantém em dia);
     * `evt.ticket_status` só corrige quando o servidor mandou. A aba não
     * entra nessa conta em hipótese nenhuma (vide histórico no git: era o
     * bug do atendimento aceito que voltava pra Pendentes).
     */
    status: evt.ticket_status ?? found.status,
    last_message_at: evt.received_at ?? evt.created_at ?? new Date().toISOString(),
    last_message_preview: bodyOrNull ?? mediaPreviewForType(evt.type) ?? found.last_message_preview,
    unread_count: nextUnread,
    ...(evt.bot_active === true && { bot_active: true }),
    // Janela 24h do CARD (countdown/"expirado" do TicketCard): INBOUND renova
    // o last_inbound_at do contato no cartão. Sem isto, o cliente respondia,
    // o composer liberava (header corrigido) mas o card seguia "expirado" até
    // um refetch de lista — furo que já existia no onInboundInbox original.
    ...(evt.direction !== 'outbound' &&
      (evt.received_at ?? evt.created_at) && {
        contact: {
          ...found.contact,
          last_inbound_at: (evt.received_at ?? evt.created_at) as string,
        },
      }),
  };
  applyTicketCard(
    qc,
    card,
    {
      id: me.id,
      profile: me.profile,
      departmentIds: me.departmentIds,
      support: Boolean(me.impersonating),
    },
    { origin: 'encaixaCardDoInbound', event: 'message.received' },
  );
  return true;
}
